import {
  AppId,
  APP_DETAILS,
  APP_ROUTE_BASE_PATHS,
  APP_KEY_BASE_PATHS,
  APP_ID_BY_SESSIONS,
} from "@dashboard/constants/appRegistry"
import { useIsOnline } from "@dashboard/features/online"
import { isOfflineReady } from "@dashboard/lib/offlineReady"
import { FC, useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { NoSubs } from "../modal/noSubs"
import { profileServices, selectedAppStore } from "@workspace/shared"
import { SimpleModal, Button } from "@workspace/ui"
import { DashboardSection } from "./dashboard-section"
import type { DashboardAppItem, DashboardGridProps, DisableReason } from "./types"

export type { AppCard, SessionsMap } from "./types"

const OFFENSE_ORDER: AppId[] = [AppId.QB, AppId.RB, AppId.TE, AppId.OL, AppId.WR]
const DEFENSE_ORDER: AppId[] = [AppId.LB, AppId.S, AppId.DL, AppId.DB, AppId.DC]
const SPECIAL_TEAMS_ORDER: AppId[] = [AppId.ST]
const PRACTICE_ORDER: AppId[] = [AppId.DEF]

const getDisplayCode = (appId: AppId, code: string) =>
  appId === AppId.DEF ? "P" : code

const DashboardGrid: FC<DashboardGridProps> = ({
  appsBySubscription,
  currentSessions,
  onSessionsRefresh,
}) => {
  const { isOnline } = useIsOnline()
  const navigate = useNavigate()
  // The tapped lock: which app + why it's locked. Drives one modal whose
  // copy/action depends on the reason (subscription / session / offline).
  const [lockInfo, setLockInfo] = useState<{
    item: DashboardAppItem
    reason: DisableReason
  } | null>(null)
  const [revokeLoading, setRevokeLoading] = useState(false)
  const [revokeError, setRevokeError] = useState<string | null>(null)

  const groupedApps = useMemo(() => {
    // Show every app in each group. Apps the coach isn't subscribed to still
    // render — greyed with a lock — so the catalog is always visible (matches
    // the in-game dashboard). Subscription status rides on each item.
    const mapApps = (appId: AppId): DashboardAppItem[] => {
      const app = APP_DETAILS[appId as Exclude<AppId, AppId.Dashboard>]
      if (!app) return []
      const card = appsBySubscription[appId]
      return [
        {
          ...app,
          appId,
          index: 0,
          count: card?.count ?? 0,
          displayCode: getDisplayCode(appId, app.code),
          isSubscribed: !!card,
        },
      ]
    }

    return {
      offense: OFFENSE_ORDER.flatMap(mapApps),
      defense: DEFENSE_ORDER.flatMap(mapApps),
      specialTeams: SPECIAL_TEAMS_ORDER.flatMap(mapApps),
      practice: PRACTICE_ORDER.flatMap(mapApps),
    }
  }, [appsBySubscription])

  // "No apps" = the coach has zero subscriptions at all → show the NoSubs view
  // rather than a grid full of locked tiles.
  const hasNoApps = Object.keys(appsBySubscription).length === 0

  // Why a tile is disabled, so the card can show the right icon:
  //  - not subscribed        -> lock (needs a subscription)
  //  - offline & no local data-> wifi (never opened online, so it has no cached
  //    settings/plays to work offline). Apps opened online once have local data
  //    and stay tappable offline.
  const getDisableReason = (item: DashboardAppItem): DisableReason => {
    if (!item.isSubscribed) return "subscription"
    if (!isOnline && !(item.isOfflineCapable && isOfflineReady(item.appId))) {
      return "offline"
    }
    // Device-session lock (mirrors the in-game dashboard): the app's device
    // limit is used up AND none of those sessions belong to this device.
    const session =
      currentSessions[
        APP_ID_BY_SESSIONS[item.appId as Exclude<AppId, AppId.Dashboard>]
      ]
    if (
      session?.sessionsAvailable === 0 &&
      session.values.find((el) => el.isCurrentDevice) === undefined
    ) {
      return "session"
    }
    return null
  }

  const handleLockClick = useCallback(
    (item: DashboardAppItem, reason: DisableReason) => {
      setRevokeError(null)
      setLockInfo({ item, reason })
    },
    [],
  )

  const closeLockModal = useCallback(() => {
    if (revokeLoading) return
    setLockInfo(null)
    setRevokeError(null)
  }, [revokeLoading])

  const handleRevokeConfirm = useCallback(async () => {
    if (!lockInfo) return

    const appName =
      APP_ID_BY_SESSIONS[lockInfo.item.appId as Exclude<AppId, AppId.Dashboard>]
    const { setSelectedAppState } = selectedAppStore.getState()

    setRevokeLoading(true)
    setRevokeError(null)

    try {
      setSelectedAppState({ key: "appKey", value: appName })
      // Use the authenticated profile id straight from the API rather than
      // any cached value — same as the in-game dashboard.
      const { data: profile } = await profileServices.onGetProfile()
      await profileServices.onRevokeLogin(profile.id)
      await onSessionsRefresh()
      setLockInfo(null)
    } catch (error: unknown) {
      setRevokeError(
        error instanceof Error
          ? error.message
          : "Failed to close sessions on other devices.",
      )
    } finally {
      setSelectedAppState({ key: "appKey", value: "Dashboard" })
      setRevokeLoading(false)
    }
  }, [onSessionsRefresh, lockInfo])

  const handleAppClick = (
    item: DashboardAppItem,
    isDisabled: boolean,
  ) => {
    if (isDisabled) return

    const appId = item.appId as Exclude<AppId, AppId.Dashboard>
    const { setSelectedAppState } = selectedAppStore.getState()
    const appRoute = APP_ROUTE_BASE_PATHS[appId]
    setSelectedAppState({
      key: "appKey",
      value: APP_KEY_BASE_PATHS[appId],
    })

    if (!appRoute) {
      console.warn(`No route configured for app: ${item.appId}`)
      return
    }

    navigate(appRoute)
  }

  useEffect(() => {
    const { setSelectedAppState } = selectedAppStore.getState()
    setSelectedAppState({ key: "appKey", value: undefined })
  }, [])

  const sections = [
    { title: "Offense", apps: groupedApps.offense },
    { title: "Defense", apps: groupedApps.defense },
    { title: "Special Teams", apps: groupedApps.specialTeams },
    { title: "Practice", apps: groupedApps.practice },
  ].filter((section) => section.apps.length > 0)

  return (
    <div>
      {hasNoApps ? (
        <NoSubs />
      ) : (
        <div className="flex w-full justify-center">
          <div className="w-full">
            {sections.map((section, index) => (
              <div key={section.title}>
                {index > 0 && <hr className="my-4 border-[#E5E5E5]" />}
                <DashboardSection
                  title={section.title}
                  apps={section.apps}
                  getDisableReason={getDisableReason}
                  onAppClick={handleAppClick}
                  onLockClick={handleLockClick}
                />
              </div>
            ))}
          </div>
        </div>
      )}
      {/* One "why is this locked" modal. The session case keeps the in-game
          takeover copy + Confirm (close other sessions); subscription and
          offline are informational. */}
      <SimpleModal
        isOpen={Boolean(lockInfo)}
        onClose={closeLockModal}
        title={
          lockInfo?.reason === "session"
            ? "Sessions on other devices"
            : lockInfo?.reason === "subscription"
              ? "Subscription Required"
              : "Not Available Offline"
        }
        showCloseButton={!revokeLoading}
        footer={
          lockInfo?.reason === "session" ? (
            <div className="flex justify-end gap-2">
              <Button
                className="btn text-primary-foreground rounded-xl shadow hover:bg-white"
                variant="ghost"
                onClick={closeLockModal}
                disabled={revokeLoading}
              >
                Cancel
              </Button>
              <Button
                variant="default"
                onClick={handleRevokeConfirm}
                disabled={revokeLoading}
                className="btn rounded-xl bg-blue-600 text-[14px] capitalize text-white"
                style={{ background: "#155dfc", color: "#ffffff" }}
              >
                {revokeLoading ? "Processing..." : "Confirm"}
              </Button>
            </div>
          ) : (
            <div className="flex justify-end">
              <Button
                variant="default"
                onClick={closeLockModal}
                className="btn rounded-xl bg-blue-600 text-[14px] capitalize text-white"
                style={{ background: "#155dfc", color: "#ffffff" }}
              >
                Go Back
              </Button>
            </div>
          )
        }
      >
        <div className="w-full text-left">
          {lockInfo?.reason === "session" ? (
            <p className="text-muted-foreground text-sm">
              You've reached the maximum number of sessions allowed for your
              account. If you continue, the active sessions for{" "}
              <strong>{lockInfo?.item.label}</strong> will be closed on other
              devices and any unsaved changes may be lost.
            </p>
          ) : lockInfo?.reason === "subscription" ? (
            <p className="text-muted-foreground text-sm">
              You don&rsquo;t have a subscription for{" "}
              <strong>{lockInfo?.item.label}</strong>. Contact your program
              administrator to add it to your account.
            </p>
          ) : (
            <p className="text-muted-foreground text-sm">
              <strong>{lockInfo?.item.label}</strong> hasn&rsquo;t been opened
              on this device while online, so it isn&rsquo;t installed for
              offline use. Connect to the internet and open it once — after
              that it will also work offline.
            </p>
          )}
          {revokeError ? (
            <p className="mt-2 text-sm text-red-600">{revokeError}</p>
          ) : null}
        </div>
      </SimpleModal>
    </div>
  )
}

export default DashboardGrid
